import { useEffect, useState } from "react";
import API from "../api/axios";
import toast from "react-hot-toast";
import Layout from "../components/Layout";
import usePlan from "../hooks/usePlan";
import UpgradePrompt from "../components/UpgradePrompt";
import { saveToCache, loadFromCache, formatDate } from "../utils/cache";
import { useTheme } from "../context/ThemeContext";

export default function Products() {
  const { theme } = useTheme();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState({
    name: "", sku: "", description: "", unit_price: "",
    cost_price: "", quantity_in_stock: "", low_stock_threshold: 10,
    unit_of_measure: "unit", category_id: "",
  });
  const [editProduct, setEditProduct] = useState(null);
  const [search, setSearch] = useState("");
  const [editForm, setEditForm] = useState({
    quantity_to_add: 0, low_stock_threshold: "",
    unit_price: "", cost_price: "", note: "",
  });
  const { isPaid, hasFullAccess, isExpired } = usePlan();

  // Shared theme styles — defined once
  const card = {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: "14px",
    padding: "28px",
    boxShadow: theme.cardShadow,
  };
  const input = {
    background: theme.inputBg,
    border: `1px solid ${theme.inputBorder}`,
    borderRadius: "10px",
    padding: "12px 16px",
    color: theme.textPrimary,
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };
  const label = { fontSize: "13px", fontWeight: "500", color: theme.textSecondary };
  const field = { display: "flex", flexDirection: "column", gap: "8px" };

  const fetchProducts = async () => {
    try {
      const [prodRes, catRes] = await Promise.all([
        API.get("/products/"),
        API.get("/categories/"),
      ]);
      setProducts(prodRes.data);
      setCategories(catRes.data);
      saveToCache("products", prodRes.data);
      saveToCache("categories", catRes.data);
    } catch {
      const cachedProducts = loadFromCache("products");
      const cachedCategories = loadFromCache("categories");
      if (cachedProducts) setProducts(cachedProducts);
      if (cachedCategories) setCategories(cachedCategories);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      unit_price: parseFloat(form.unit_price),
      cost_price: parseFloat(form.cost_price),
      quantity_in_stock: parseInt(form.quantity_in_stock),
      low_stock_threshold: parseInt(form.low_stock_threshold),
    };
    try {
      await API.post("/products/", payload);
      toast.success("Product added");
    } catch {
      const { addToQueue } = await import("../utils/offlineQueue");
      await addToQueue({ method: "POST", url: "/products/", data: payload });
      const tempProduct = {
        ...payload,
        id: `offline_${Date.now()}`,
        created_at: new Date().toISOString(),
        category_name: categories.find(c => c.id === payload.category_id)?.name || null,
      };
      setProducts(prev => [tempProduct, ...prev]);
      saveToCache("products", [tempProduct, ...products]);
      toast.success("No internet — product saved offline and will sync automatically", { icon: "📵" });
    }
    setShowForm(false);
    setForm({
      name: "", sku: "", description: "", unit_price: "",
      cost_price: "", quantity_in_stock: "", low_stock_threshold: 10,
      unit_of_measure: "unit", category_id: "",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await API.delete(`/products/${id}`);
      toast.success("Product deleted");
      fetchProducts();
    } catch {
      toast.error("Failed to delete product");
    }
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await API.post("/products/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message);
      if (res.data.errors.length > 0) res.data.errors.forEach((err) => toast.error(err));
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const downloadTemplate = () => {
  const csv = [
    "name,sku,description,unit_price,cost_price,quantity_in_stock,low_stock_threshold,unit_of_measure,category",
    "Red T-Shirt,TSH-001,Cotton red t-shirt,5000,3000,100,10,piece,Clothing",
    "Blue Jeans,JNS-001,Slim fit blue jeans,15000,9000,50,5,piece,Clothing",
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "martsense-products-template.csv";
  a.click();
  URL.revokeObjectURL(url);
};

  const handleExportProducts = async () => {
    try {
      const response = await API.get("/reports/export/products", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "products-export.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Products exported successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    }
  };

  const handleEdit = (product) => {
    setEditProduct(product);
    setEditForm({
      quantity_to_add: 0,
      low_stock_threshold: product.low_stock_threshold,
      unit_price: product.unit_price,
      cost_price: product.cost_price,
      category_id: product.category_id || "",
      note: "",
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await API.patch(`/products/${editProduct.id}/adjust`, {
        quantity_to_add: parseInt(editForm.quantity_to_add) || 0,
        low_stock_threshold: parseInt(editForm.low_stock_threshold),
        unit_price: parseFloat(editForm.unit_price),
        cost_price: parseFloat(editForm.cost_price),
        category_id: editForm.category_id || null,
        note: editForm.note,
      });
      toast.success(res.data.message);
      setEditProduct(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const displayedProducts = (isExpired ? products.slice(0, 10) : products)
    .filter((p) =>
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.sku?.toLowerCase().includes(search.toLowerCase()) ||
      p.category_name?.toLowerCase().includes(search.toLowerCase())
    );

  return (
    <Layout>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px", flexWrap: "wrap", gap: "16px" }}>
        <div>
          <h1 style={{ fontSize: "28px", fontWeight: "700", color: theme.textPrimary, letterSpacing: "-0.5px" }}>Products</h1>
          <p style={{ fontSize: "14px", color: theme.textSecondary, marginTop: "4px" }}>
            {search ? `${displayedProducts.length} of ${products.length} products` : `${products.length} products`}
          </p>
        </div>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          {isPaid && (
            <button onClick={handleExportProducts} style={{
              background: "none", border: `1px solid #2ec4b6`,
              borderRadius: "10px", padding: "12px 20px",
              color: "#2ec4b6", fontSize: "14px", fontWeight: "600", cursor: "pointer",
            }}>⬇ Export CSV</button>
          )}
          {hasFullAccess && (
            <button onClick={downloadTemplate} style={{
              background: "none", border: `1px solid ${theme.cardBorder}`,
              borderRadius: "10px", padding: "12px 20px",
              color: theme.textSecondary, fontSize: "14px", fontWeight: "600", cursor: "pointer",
            }}>⬇ Template</button>
          )}
          {hasFullAccess && (
            <label style={{
              background: theme.inputBg, border: `1px solid #2ec4b6`,
              borderRadius: "10px", padding: "12px 20px",
              color: "#2ec4b6", fontSize: "14px", fontWeight: "600",
              cursor: "pointer", display: "inline-block",
            }}>
              {importing ? "Importing..." : "⬆ Import CSV"}
              <input type="file" accept=".csv" onChange={handleCSVImport}
                style={{ display: "none" }} disabled={importing} />
            </label>
          )}
          <button onClick={() => setShowForm(!showForm)} style={{
            background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
            color: "#0a0f0f", border: "none", borderRadius: "10px",
            padding: "12px 20px", fontSize: "14px", fontWeight: "700", cursor: "pointer",
          }}>
            {showForm ? "Cancel" : "+ Add Product"}
          </button>
        </div>
      </div>

      {isExpired && (
        <UpgradePrompt
          feature="Full Inventory Access"
          message="You are viewing up to 10 products. Subscribe to manage unlimited products and unlock all features."
        />
      )}

      {/* Add Product Form */}
      {showForm && hasFullAccess && (
        <div style={{ ...card, marginBottom: "32px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: "600", color: theme.textPrimary, marginBottom: "24px" }}>
            New Product
          </h2>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" }}>
              {[
                { label: "Product Name *", name: "name", type: "text", required: true },
                { label: "SKU", name: "sku", type: "text" },
                { label: "Selling Price (₦) *", name: "unit_price", type: "number", required: true },
                { label: "Cost Price (₦) *", name: "cost_price", type: "number", required: true },
                { label: "Quantity *", name: "quantity_in_stock", type: "number", required: true },
                { label: "Low Stock Alert At", name: "low_stock_threshold", type: "number" },
                { label: "Unit of Measure", name: "unit_of_measure", type: "text" },
                { label: "Description", name: "description", type: "text" },
              ].map((f) => (
                <div key={f.name} style={field}>
                  <label style={label}>{f.label}</label>
                  <input
                    name={f.name} type={f.type}
                    value={form[f.name]} onChange={handleChange}
                    required={f.required} style={input}
                  />
                </div>
              ))}
              <div style={field}>
                <label style={label}>Category</label>
                <select name="category_id" value={form.category_id || ""} onChange={handleChange} style={input}>
                  <option value="">No category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <button type="submit" style={{
              background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
              color: "#0a0f0f", border: "none", borderRadius: "10px",
              padding: "12px 32px", fontSize: "14px", fontWeight: "700", cursor: "pointer",
            }}>Save Product</button>
          </form>
        </div>
      )}

      {/* Search */}
      {products.length > 0 && (
        <div style={{ position: "relative", marginBottom: "20px", maxWidth: "400px" }}>
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...input, padding: "12px 40px 12px 16px" }}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{
              position: "absolute", right: "12px", top: "50%",
              transform: "translateY(-50%)", background: "none",
              border: "none", color: theme.textSecondary, fontSize: "16px", cursor: "pointer",
            }}>✕</button>
          )}
        </div>
      )}

      {/* Products Table */}
      {loading ? (
        <p style={{ color: theme.textSecondary }}>Loading...</p>
      ) : displayedProducts.length === 0 ? (
        <div style={{ textAlign: "center", color: theme.textSecondary, padding: "60px 0" }}>
          <p>{search ? "No products match your search." : "No products yet. Add your first product above."}</p>
        </div>
      ) : (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
          borderRadius: "14px", overflowX: "auto",
          WebkitOverflowScrolling: "touch", boxShadow: theme.cardShadow,
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "700px" }}>
            <thead>
              <tr>
                {["Name", "SKU", "Category", "Price", "Cost", "Stock", "Unit", "Date Added", "Action"].map((h) => (
                  <th key={h} style={{
                    textAlign: "left", padding: "14px 16px",
                    fontSize: "12px", fontWeight: "600",
                    color: theme.textSecondary, textTransform: "uppercase",
                    letterSpacing: "0.5px", borderBottom: `1px solid ${theme.divider}`,
                    background: theme.cardBg,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayedProducts.map((p) => (
                <tr key={p.id} style={{ borderBottom: `1px solid ${theme.divider}` }}>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textPrimary }}>{p.name}</td>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textSecondary, fontFamily: "monospace" }}>
                    {p.sku || "—"}
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textSecondary }}>
                    {p.category_name || "—"}
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textPrimary }}>
                    ₦{p.unit_price.toLocaleString()}
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textPrimary }}>
                    ₦{p.cost_price.toLocaleString()}
                  </td>
                  <td style={{
                    padding: "14px 16px", fontSize: "14px", fontWeight: 600,
                    color: p.quantity_in_stock <= p.low_stock_threshold ? "#e74c3c" : "#2ec4b6",
                  }}>
                    {p.quantity_in_stock}
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textSecondary }}>
                    {p.unit_of_measure}
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textSecondary, whiteSpace: "nowrap" }}>
                    {formatDate(p.created_at)}
                  </td>
                  <td style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button onClick={() => handleEdit(p)} style={{
                        background: "none", border: "1px solid #2ec4b6",
                        borderRadius: "6px", color: "#2ec4b6",
                        padding: "6px 12px", fontSize: "12px",
                        fontWeight: "600", cursor: "pointer",
                      }}>Edit</button>
                      {hasFullAccess && (
                        <button onClick={() => handleDelete(p.id)} style={{
                          background: "none", border: "1px solid #e74c3c",
                          borderRadius: "6px", color: "#e74c3c",
                          padding: "6px 12px", fontSize: "12px",
                          fontWeight: "600", cursor: "pointer",
                        }}>Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {editProduct && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
          zIndex: 500, display: "flex", alignItems: "center",
          justifyContent: "center", padding: "20px",
        }} onClick={() => setEditProduct(null)}>
          <div style={{
            background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
            borderRadius: "16px", padding: "32px",
            width: "100%", maxWidth: "500px", maxHeight: "90vh", overflowY: "auto",
            boxShadow: theme.cardShadow,
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
              <h2 style={{ fontSize: "18px", fontWeight: "700", color: theme.textPrimary }}>
                Edit — {editProduct.name}
              </h2>
              <button onClick={() => setEditProduct(null)} style={{
                background: "none", border: "none",
                color: theme.textSecondary, fontSize: "20px", cursor: "pointer",
              }}>✕</button>
            </div>
            <form onSubmit={handleEditSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div style={field}>
                <label style={label}>Add Stock (use negative to reduce)</label>
                <input
                  type="number"
                  value={editForm.quantity_to_add}
                  onChange={(e) => setEditForm({ ...editForm, quantity_to_add: e.target.value })}
                  style={input} placeholder="0"
                />
                <p style={{ fontSize: "11px", color: theme.textSecondary, marginTop: "4px" }}>
                  Current stock: {editProduct.quantity_in_stock}
                </p>
              </div>
              <div style={field}>
                <label style={label}>Low Stock Threshold</label>
                <input type="number" value={editForm.low_stock_threshold}
                  onChange={(e) => setEditForm({ ...editForm, low_stock_threshold: e.target.value })}
                  style={input} />
              </div>
              <div style={field}>
                <label style={label}>Selling Price (₦)</label>
                <input type="number" value={editForm.unit_price}
                  onChange={(e) => setEditForm({ ...editForm, unit_price: e.target.value })}
                  style={input} />
              </div>
              <div style={field}>
                <label style={label}>Cost Price (₦)</label>
                <input type="number" value={editForm.cost_price}
                  onChange={(e) => setEditForm({ ...editForm, cost_price: e.target.value })}
                  style={input} />
              </div>
              <div style={field}>
                <label style={label}>Category</label>
                <select value={editForm.category_id || ""}
                  onChange={(e) => setEditForm({ ...editForm, category_id: e.target.value })}
                  style={input}>
                  <option value="">No Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div style={field}>
                <label style={label}>Note (optional)</label>
                <input type="text" value={editForm.note}
                  onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                  placeholder="Reason for adjustment" style={input} />
              </div>
              <div style={{ display: "flex", gap: "12px" }}>
                <button type="submit" style={{
                  background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
                  color: "#0a0f0f", border: "none", borderRadius: "10px",
                  padding: "12px 32px", fontSize: "14px", fontWeight: "700", cursor: "pointer",
                }}>Save Changes</button>
                <button type="button" onClick={() => setEditProduct(null)} style={{
                  background: "none", border: `1px solid ${theme.cardBorder}`,
                  borderRadius: "10px", padding: "12px 24px",
                  color: theme.textSecondary, fontSize: "14px", cursor: "pointer",
                }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}